import { useId, type HTMLAttributes, type Ref } from 'react';
import clsx from 'clsx';
import './HeartsBar.css';

export interface HeartsBarProps extends HTMLAttributes<HTMLSpanElement> {
  /** How many hearts the user currently has. */
  current: number;
  /** Total heart slots (default 5). */
  total?: number;
  size?: 'sm' | 'md' | 'lg';
  ref?: Ref<HTMLSpanElement>;
}

/**
 * HeartsBar — row of heart icons, filled for lives remaining.
 *
 * ⚠ Built unused in MVP (DECISIONS.md: no hearts in v1).
 */
export function HeartsBar({ current, total = 5, size = 'md', className, ref, ...rest }: HeartsBarProps) {
  const safeCurrent = Math.max(0, Math.min(total, current));
  return (
    <span
      ref={ref}
      className={clsx('fluent-hearts', className)}
      data-size={size}
      aria-label={`${safeCurrent}/${total} გული`}
      {...rest}
    >
      {Array.from({ length: total }, (_, i) => {
        const filled = i < safeCurrent;
        return (
          <span key={i} className="fluent-hearts__heart" data-filled={filled} aria-hidden="true">
            {filled ? <HeartFilledIcon /> : <HeartOutlineIcon />}
          </span>
        );
      })}
    </span>
  );
}

function HeartFilledIcon() {
  // useId() emits colons (":r0:") which break SVG url(#...) refs — sanitize.
  const raw = useId().replace(/:/g, '');
  const gradId = `heart-g-${raw}`;
  const shadowId = `heart-s-${raw}`;
  const heartPath =
    'M12 21s-7.5-4.5-9.5-9.5c-1-2.5.5-6 3.5-6 2 0 3.5 1.2 4.5 2.5 1-1.3 2.5-2.5 4.5-2.5 3 0 4.5 3.5 3.5 6C19.5 16.5 12 21 12 21z';
  return (
    <svg viewBox="0 0 24 24" stroke="none">
      <defs>
        {/* Body gradient: brighter top, slightly deeper bottom for 3D roundness */}
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="1" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0.72" />
        </linearGradient>
        {/* Inner "bottom shadow" gradient — darker bottom curl for depth */}
        <radialGradient id={shadowId} cx="50%" cy="100%" r="65%">
          <stop offset="0" stopColor="#000" stopOpacity="0.28" />
          <stop offset="1" stopColor="#000" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Dropped base for outer pop */}
      <path d={heartPath} fill={`url(#${gradId})`} />
      {/* Inner depth shadow */}
      <path d={heartPath} fill={`url(#${shadowId})`} />
      {/* Glossy top-left highlight — a small elliptical shine */}
      <ellipse cx="8.5" cy="9" rx="2.8" ry="1.8" fill="#ffffff" fillOpacity="0.55" transform="rotate(-30 8.5 9)" />
      {/* Subtle bottom rim to anchor the shape */}
      <path
        d={heartPath}
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.22"
        strokeWidth="0.6"
      />
    </svg>
  );
}

function HeartOutlineIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s-7.5-4.5-9.5-9.5c-1-2.5.5-6 3.5-6 2 0 3.5 1.2 4.5 2.5 1-1.3 2.5-2.5 4.5-2.5 3 0 4.5 3.5 3.5 6C19.5 16.5 12 21 12 21z" />
    </svg>
  );
}
